import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { Tag, TagTarget } from '../../../app/entities';
import { Model } from '../../../core/engine';
import { Point, TagLabel } from './styled-tags-components';
import { TagSelector } from './tag-selector';
import { context } from '../../../app/context';

type TagsViewerProps = {
  targetType: TagTarget;
  targetId?: string;
  onSelect?: (tagsIds: string[]) => void;
  tagsIds?: string[];
  onMoreClick?: () => void;
  overflow?: 'wrap' | 'collapse';
  alignment?: 'start' | 'end';
  fullWidth?: boolean;
};

type TagTargetEntity = {
  tags: Tag[];
  tagsIds: string[];
  updateTags: (tagsIds: string[]) => Promise<void>;
} & Model;

export const TagsViewer: React.FC<TagsViewerProps> = observer(
  ({
    targetId,
    targetType,
    onSelect,
    tagsIds,
    overflow = 'wrap',
    alignment = 'start',
    fullWidth = true,
  }) => {
    const targetEntity = targetId ? (Model.getOne(targetId) as TagTargetEntity) : null;
    let tags = targetEntity?.tags || [];

    const selectedTags = tagsIds ? tagsIds : tags.map((tag) => tag._id);

    const space = context.space;

    if (tagsIds) {
      tags = space.tags.filter((el) => tagsIds.includes(el._id));
    }

    const handleChange = (tags: string[]) => {
      onSelect ? onSelect(tags) : targetEntity?.updateTags(tags);
    };

    return (
      <Container data-align={alignment} data-full-width={fullWidth}>
        <TagsContainer data-wrap={overflow === 'wrap'}>
          {tags.map((tag) => (
            <TagSelector
              key={tag._id}
              targetType={targetType}
              selectedTags={selectedTags}
              onChange={handleChange}
              trigger={() => (
                <TagLabel
                  onClick={(e) => e.stopPropagation()}
                  key={tag._id}
                  data-visible={true}
                  color={tag.color}
                >
                  <Point color={tag.color} />
                  <span>{tag.text}</span>
                </TagLabel>
              )}
            />
          ))}

          <TagSelector
            targetType={targetType}
            selectedTags={selectedTags}
            onChange={handleChange}
          />
        </TagsContainer>
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;

  &[data-align='end'] {
    justify-content: flex-end;
  }

  &[data-full-width='true'] {
    width: 100%;
  }
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.4rem;
  min-width: 0;
  overflow: hidden;

  &[data-wrap='true'] {
    flex-wrap: wrap;
  }
`;
